import React, { useState, useEffect } from 'react';
import { FlagIcon, XMarkIcon, CheckIcon, EyeIcon, ArrowPathIcon, PencilIcon } from '@heroicons/react/24/solid';
import { questionFlagApi } from '../utils/api';
import Pagination from '../components/Pagination';
import type { QuestionFlagItem, FlagStatus } from '../types/questionFlag';
import { getFlagStatusColor, getFlagStatusLabel } from '../types/questionFlag';

const PAGE_SIZE = 10;

const FlagToAuthor: React.FC = () => {
  const [flags, setFlags] = useState<QuestionFlagItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<QuestionFlagItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [moderatorReason, setModeratorReason] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editStatus, setEditStatus] = useState<FlagStatus>('Approved');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<FlagStatus | ''>('');

  useEffect(() => {
    fetchFlags();
  }, [currentPage, statusFilter]);

  useEffect(() => {
    if (feedback?.type === 'success') {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const fetchFlags = async () => {
    setIsLoading(true);
    try {
      const response = await questionFlagApi.getAllFlags(
        statusFilter || undefined,
        currentPage,
        PAGE_SIZE
      );
      if (response.success && response.data) {
        setFlags(response.data.flags || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setFeedback({ type: 'error', text: response.message || 'Failed to load flags' });
        setFlags([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching flags:', error);
      setFeedback({ type: 'error', text: 'Failed to load flags' });
      setFlags([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedFlag(null);
    setModeratorReason('');
    setIsEditMode(false);
  };

  const saveFlagStatus = async (flagId: string, newStatus: FlagStatus, confirmMessage?: string) => {
    const trimmedReason = moderatorReason.trim();
    if (!trimmedReason) {
      setFeedback({ type: 'error', text: 'Please provide a moderator response before saving.' });
      return;
    }

    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }

    setIsUpdating(true);
    try {
      const response = await questionFlagApi.updateFlagStatus(flagId, {
        status: newStatus,
        reason: trimmedReason,
      });
      if (response.success) {
        setFeedback({ type: 'success', text: `Flag updated to ${newStatus}` });
        closeModal();
        fetchFlags();
      } else {
        setFeedback({ type: 'error', text: response.message || 'Failed to update flag' });
      }
    } catch (error) {
      console.error('Error updating flag:', error);
      setFeedback({ type: 'error', text: 'Failed to update flag' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateStatus = (flagId: string, newStatus: FlagStatus) => {
    saveFlagStatus(flagId, newStatus, `Are you sure you want to mark this flag as ${newStatus}?`);
  };

  const handleSaveEdit = () => {
    if (!selectedFlag) return;
    saveFlagStatus(selectedFlag.id, editStatus, 'Save changes to this flag?');
  };

  const loadFlagDetails = async (flag: QuestionFlagItem) => {
    try {
      const response = await questionFlagApi.getFlagById(flag.id);
      if (response.success && response.data) {
        setSelectedFlag(response.data);
        setModeratorReason(response.data.reason || '');
        if (response.data.status === 'Approved' || response.data.status === 'Rejected') {
          setEditStatus(response.data.status as FlagStatus);
        }
      }
    } catch (error) {
      console.error('Error fetching flag details:', error);
    }
  };

  const handleViewFlag = async (flag: QuestionFlagItem) => {
    setSelectedFlag(flag);
    setModeratorReason(flag.reason || '');
    setIsEditMode(false);
    setShowDetailModal(true);
    await loadFlagDetails(flag);
  };

  const handleEditFlag = async (flag: QuestionFlagItem) => {
    setSelectedFlag(flag);
    setModeratorReason(flag.reason || '');
    setEditStatus(flag.status === 'Rejected' ? 'Rejected' : 'Approved');
    setIsEditMode(true);
    setShowDetailModal(true);
    await loadFlagDetails(flag);
  };

  const hasResolvedStatus = (status: string) => status === 'Approved' || status === 'Rejected';

  const handleFilterChange = (newStatus: FlagStatus | '') => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setStatusFilter('');
    setCurrentPage(1);
  };

  if (isLoading && flags.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FlagIcon className="h-8 w-8 mr-2 text-blue-600" />
            Flag to Author
          </h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {totalCount} total flag{totalCount === 1 ? '' : 's'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange(e.target.value as FlagStatus | '')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="New">New</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          <button
            onClick={handleReset}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            title="Reset filters"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {feedback && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            feedback.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-center">
            {feedback.type === 'success' ? (
              <CheckIcon className="h-5 w-5 mr-2" />
            ) : (
              <XMarkIcon className="h-5 w-5 mr-2" />
            )}
            <p className="text-sm font-medium">{feedback.text}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lesson</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Comment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Moderator Response</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {flags.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No flags found
                  </td>
                </tr>
              ) : (
                flags.map((flag) => (
                  <tr key={flag.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{flag.lessonName}</div>
                      <div className="text-xs text-gray-500">Q{flag.questionNumber}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {flag.question?.text || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{flag.studentName || flag.studentId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">{flag.studentComment || '—'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">{flag.reason || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getFlagStatusColor(flag.status)}`}
                      >
                        {getFlagStatusLabel(flag.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(flag.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleViewFlag(flag)}
                          className="text-blue-600 hover:text-blue-900"
                          aria-label="View flag details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {hasResolvedStatus(flag.status) && (
                          <button
                            onClick={() => handleEditFlag(flag)}
                            className="text-amber-600 hover:text-amber-900"
                            aria-label="Edit flag status and response"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {showDetailModal && selectedFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {isEditMode ? 'Edit Flag' : 'Flag Details'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lesson</label>
                    <p className="text-sm text-gray-900">{selectedFlag.lessonName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question #</label>
                    <p className="text-sm text-gray-900">{selectedFlag.questionNumber}</p>
                  </div>
                </div>

                {selectedFlag.question?.text && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                      {selectedFlag.question.text}
                    </p>
                    {selectedFlag.question.type && (
                      <p className="text-xs text-gray-500 mt-1">Type: {selectedFlag.question.type}</p>
                    )}
                  </div>
                )}

                {selectedFlag.question?.options && selectedFlag.question.options.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                    <ul className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md space-y-1">
                      {selectedFlag.question.options.map((opt, idx) => (
                        <li key={idx}>
                          {typeof opt.text === 'string' ? opt.text : JSON.stringify(opt)}
                          {opt.isCorrect ? ' ✓' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
                  <p className="text-sm text-gray-900">{selectedFlag.studentName || selectedFlag.studentId}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Student Comment</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                    {selectedFlag.studentComment || '—'}
                  </p>
                </div>

                {!isEditMode && hasResolvedStatus(selectedFlag.status) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Moderator Response</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                      {selectedFlag.reason || '—'}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  {isEditMode ? (
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value as FlagStatus)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  ) : (
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getFlagStatusColor(selectedFlag.status)}`}
                    >
                      {getFlagStatusLabel(selectedFlag.status)}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedFlag.createdAt).toLocaleString()}
                  </p>
                </div>

                {(selectedFlag.status === 'New' || isEditMode) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="moderator-reason">
                      Moderator Response
                    </label>
                    <textarea
                      id="moderator-reason"
                      value={moderatorReason}
                      onChange={(e) => setModeratorReason(e.target.value)}
                      rows={3}
                      placeholder="e.g. The correct answer is option B. We updated the question."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Required. This response is shared with the student.
                    </p>
                  </div>
                )}

                {selectedFlag.status === 'New' && !isEditMode && (
                  <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(selectedFlag.id, 'Rejected')}
                      disabled={isUpdating}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? 'Updating...' : 'Reject'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(selectedFlag.id, 'Approved')}
                      disabled={isUpdating}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? 'Updating...' : 'Approve'}
                    </button>
                  </div>
                )}

                {isEditMode && (
                  <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={closeModal}
                      disabled={isUpdating}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={isUpdating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}

                {!isEditMode && hasResolvedStatus(selectedFlag.status) && (
                  <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setEditStatus(selectedFlag.status as FlagStatus);
                        setIsEditMode(true);
                      }}
                      className="flex items-center px-4 py-2 bg-amber-50 text-amber-800 rounded-md hover:bg-amber-100"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit Status & Response
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlagToAuthor;
